// src/components/ui/controls/DNSRecordRow/DNSRecordRow.jsx
import React, { useState } from 'react';
import { Copy, Check, AlertCircle, CheckCircle } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import Button from '../../core/Button';

const DNSRecordRow = ({
  type = 'CNAME',
  host = '',
  value = '',
  status = 'pending', // 'pending', 'verified', 'failed'
  description = '',
  className = ''
}) => {
  const [copied, setCopied] = useState(null); // 'host', 'value', or null

  // Handle copy to clipboard
  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Status configuration
  const getStatusConfig = () => {
    switch (status) {
      case 'verified':
        return {
          icon: CheckCircle,
          color: 'text-emerald-400',
          bgColor: 'bg-emerald-500/20',
          label: 'Verified'
        };
      case 'failed':
        return {
          icon: AlertCircle,
          color: 'text-red-400',
          bgColor: 'bg-red-500/20',
          label: 'Failed'
        };
      default:
        return {
          icon: AlertCircle,
          color: 'text-amber-400',
          bgColor: 'bg-amber-500/20',
          label: 'Pending'
        };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  return (
    <div className={cn(
      'p-4 bg-slate-800/30 border border-slate-700/50 rounded-xl',
      className
    )}>
      {/* Header with type and status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-sm font-medium">
            {type}
          </div>
          <h4 className="text-slate-200 font-medium">
            {description || `${type} Record`}
          </h4>
        </div>
        
        <div className={cn(
          'flex items-center gap-2 px-2 py-1 rounded text-sm font-medium',
          statusConfig.bgColor
        )}>
          <StatusIcon className={cn('w-4 h-4', statusConfig.color)} />
          <span className={statusConfig.color}>{statusConfig.label}</span>
        </div>
      </div>

      {/* DNS Record Fields */}
      <div className="space-y-3">
        {/* Host/Name Field */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Host/Name
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-600/50 rounded-lg">
              <code className="text-slate-200 text-sm break-all">
                {host || 'N/A'}
              </code>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleCopy(host, 'host')}
              disabled={!host}
              className="min-w-[80px]"
            >
              {copied === 'host' ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Value Field */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Value/Target
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-600/50 rounded-lg">
              <code className="text-slate-200 text-sm break-all">
                {value || 'N/A'}
              </code>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleCopy(value, 'value')}
              disabled={!value}
              className="min-w-[80px]"
            >
              {copied === 'value' ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* TTL Note */}
      <div className="mt-3 text-xs text-slate-400">
        TTL: 300 seconds (or your DNS provider's default)
      </div>
    </div>
  );
};

export default DNSRecordRow;